/**
 * Secure file download utilities
 * Provides XSS-safe blob URL handling for file downloads
 */

/**
 * Validates that a URL is a legitimate blob URL
 * @param url - The URL to validate
 * @returns true if the URL is a valid blob URL
 */
export const isValidBlobUrl = (url: string): boolean => {
  if (!url || typeof url !== "string") {
    return false;
  }

  // Blob URLs must start with 'blob:' followed by the origin
  if (!url.startsWith("blob:")) {
    return false;
  }

  try {
    // Validate URL structure
    const blobUrl = new URL(url);

    return blobUrl.protocol === "blob:";
  } catch {
    return false;
  }
};

/**
 * Sanitizes a filename by removing potentially dangerous characters
 * @param filename - The filename to sanitize
 * @returns Sanitized filename safe for use in downloads
 */
export const sanitizeFilename = (filename: string): string => {
  if (!filename || typeof filename !== "string") {
    return "download";
  }

  return filename.replace(/[^a-zA-Z0-9._-]/g, "_");
};

/**
 * Triggers a secure file download using a validated blob URL
 * @param blob - The Blob object to download
 * @param filename - The filename for the download (will be sanitized)
 * @throws Error if blob is invalid
 */
export const triggerSecureDownload = (blob: Blob, filename: string): void => {
  // Validate blob
  if (!(blob instanceof Blob)) {
    throw new Error("Invalid blob provided for download");
  }

  // Sanitize filename
  const safeFilename = sanitizeFilename(filename);

  // Create blob URL
  const blobUrl = URL.createObjectURL(blob);

  // Validate the created blob URL
  if (!isValidBlobUrl(blobUrl)) {
    URL.revokeObjectURL(blobUrl);
    throw new Error("Failed to create valid blob URL");
  }

  try {
    // Create and configure anchor element
    const link = document.createElement("a");

    link.setAttribute("href", blobUrl);
    link.setAttribute("download", safeFilename);

    // Append to body, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Always cleanup blob URL
    URL.revokeObjectURL(blobUrl);
  }
};
